import { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Checkbox } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import { CloseOutlined, SearchOutlined } from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";

const CandidateCard = ({ candidate, isSelected, onSelect }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E1F0FF] mb-3">
    <div className="flex items-center gap-4">
      <Avatar src={candidate.avatar} size="large" shape="circle" />
      <div className="flex flex-col">
        <span className="text-regular font-semibold text-darkGrey">
          {candidate.name}
        </span>
        <span className="text-small text-gray-500">{candidate.party}</span>
      </div>
    </div>
    <Checkbox checked={isSelected} onChange={() => onSelect(candidate.id)} />
  </div>
);

const AddCandidateModal = ({ isVisible, onClose, onCandidatesAdded }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  // Mock data for all position types - in real app this would come from backend API
  const candidatesByPosition = {
    Presidential: [
      {
        id: 1,
        name: "Bola Ahmed Tinubu",
        party: "APC",
        state: "Lagos",
        position: "Presidential",
        avatar: null,
      },
      {
        id: 2,
        name: "Atiku Abubakar",
        party: "PDP",
        state: "Adamawa",
        position: "Presidential",
        avatar: null,
      },
      {
        id: 3,
        name: "Peter Obi",
        party: "Labour",
        state: "Anambra",
        position: "Presidential",
        avatar: null,
      },
    ],
    Gubernatorial: [
      {
        id: 4,
        name: "Babajide Sanwo-Olu",
        party: "APC",
        state: "Lagos",
        position: "Gubernatorial",
        avatar: null,
      },
      {
        id: 5,
        name: "Abba Yusuf",
        party: "NNPP",
        state: "Kano",
        position: "Gubernatorial",
        avatar: null,
      },
      {
        id: 6,
        name: "Charles Soludo",
        party: "APGA",
        state: "Anambra",
        position: "Gubernatorial",
        avatar: null,
      },
    ],
    Senatorial: [
      {
        id: 7,
        name: "Ahmed Lawan",
        party: "APC",
        state: "Yobe",
        position: "Senatorial",
        avatar: null,
      },
      {
        id: 8,
        name: "Oluremi Tinubu",
        party: "APC",
        state: "Lagos",
        position: "Senatorial",
        avatar: null,
      },
      {
        id: 9,
        name: "Godswill Akpabio",
        party: "APC",
        state: "Akwa Ibom",
        position: "Senatorial",
        avatar: null,
      },
    ],
    "National Assembly": [
      {
        id: 10,
        name: "Femi Gbajabiamila",
        party: "APC",
        state: "Lagos",
        position: "National Assembly",
        avatar: null,
      },
      {
        id: 11,
        name: "Ahmad Lawan",
        party: "APC",
        state: "Yobe",
        position: "National Assembly",
        avatar: null,
      },
      {
        id: 12,
        name: "Ndudi Elumelu",
        party: "PDP",
        state: "Delta",
        position: "National Assembly",
        avatar: null,
      },
    ],
    Chairmanship: [
      {
        id: 13,
        name: "Ibrahim Hassan",
        party: "APC",
        state: "Kano",
        position: "Chairmanship",
        avatar: null,
      },
      {
        id: 14,
        name: "Bola Tijani",
        party: "PDP",
        state: "Oyo",
        position: "Chairmanship",
        avatar: null,
      },
      {
        id: 15,
        name: "Adewale Johnson",
        party: "APC",
        state: "Lagos",
        position: "Chairmanship",
        avatar: null,
      },
    ],
    Councillorship: [
      {
        id: 16,
        name: "Aisha Mohammed",
        party: "APC",
        state: "Kaduna",
        position: "Councillorship",
        avatar: null,
      },
      {
        id: 17,
        name: "Emmanuel Okafor",
        party: "PDP",
        state: "Enugu",
        position: "Councillorship",
        avatar: null,
      },
      {
        id: 18,
        name: "Funke Akindele",
        party: "PDP",
        state: "Lagos",
        position: "Councillorship",
        avatar: null,
      },
    ],
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleProceed = () => {
    if (selectedCandidates.length > 0) {
      setIsDeleteModalVisible(true);
    }
  };

  const getAllCandidates = () => {
    return Object.values(candidatesByPosition).flat();
  };

  const getSelectedCandidatesDetails = () => {
    const allCandidates = getAllCandidates();
    return selectedCandidates.map((id) =>
      allCandidates.find((candidate) => candidate.id === id)
    );
  };

  const createConfirmationMessage = () => {
    const selectedCandidatesDetails = getSelectedCandidatesDetails();
    const candidatesList = selectedCandidatesDetails
      .map(
        (candidate, index) =>
          `<li style="margin-bottom: 8px; font-weight: 600; color: var(--veegil-color-dark-gray)">
           ${index + 1}. ${candidate.name} - ${candidate.party}
         </li>`
      )
      .join("");

    return `
      <div style="text-align: center;">
        <span style="color: black;">
          Are you sure you want to add the following candidates?
        </span>
        <br/><br/>
        <ul style="list-style: none; text-align: center;">
          ${candidatesList}
        </ul>
      </div>
    `;
  };

  const handleSelectionProceed = () => {
    // Get all selected candidates with their full details
    const selectedCandidatesDetails = getSelectedCandidatesDetails();

    setIsDeleteModalVisible(false);
    onClose();
    showLoader();

    setTimeout(() => {
      hideLoader();
      setIsSuccessModalVisible(true);

      // Pass the selected candidates to the parent component
      if (onCandidatesAdded && selectedCandidatesDetails.length > 0) {
        onCandidatesAdded(selectedCandidatesDetails);
      }
    }, 2000);
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 px-6 flex justify-between items-center">
            <h2 className="text-bold-x text-primary">Add New Candidate</h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-lighter-blue)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Content Area */}
          <div className="flex flex-col px-6 pb-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Search Input */}
            <Input
              placeholder="Search Candidate"
              icon={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Render all position sections */}
            {Object.entries(candidatesByPosition).map(
              ([position, candidates]) => (
                <div key={position}>
                  <h3 className="text-darkGrey text-bold font-semibold mb-4">
                    TOP {position.toUpperCase()} CANDIDATES
                  </h3>
                  <div className="space-y-2">
                    {candidates
                      .filter(
                        (candidate) =>
                          searchQuery === "" ||
                          candidate.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          candidate.party
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map((candidate) => (
                        <CandidateCard
                          key={candidate.id}
                          candidate={candidate}
                          isSelected={selectedCandidates.includes(candidate.id)}
                          onSelect={handleSelectCandidate}
                        />
                      ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* Footer Buttons */}
          <div className="w-full bg-lighterBlue p-4 flex gap-3">
            <Button
              text="CANCEL"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
            />
            <Button
              text="PROCEED"
              color="primary"
              className="flex-1 bg-primary text-white"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleProceed}
              disabled={selectedCandidates.length === 0}
            />
          </div>
        </div>
      </Modal>

      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleSelectionProceed}
        header="Proceed with Selection?"
        paragraph={createConfirmationMessage()}
        proceedButtonText="PROCEED"
        cancelButtonText="CANCEL"
      />
      <Success
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        header="Candidates Added!"
        paragraph="You can now monitor your candidate's information directly on your account."
        buttonText="CLOSE"
        onButtonClick={() => setIsSuccessModalVisible(false)}
      />
    </>
  );
};

CandidateCard.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    party: PropTypes.string,
    avatar: PropTypes.string,
    state: PropTypes.string,
  }),
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

AddCandidateModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onCandidatesAdded: PropTypes.func,
};

export default AddCandidateModal;
